import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import useDetectFilterChange from "hooks/useDetectFilterChange";
import { EntityNavigatorParams } from "hooks/useEntityNavigator";
import useFindingColumns from "hooks/useFindingColumns";
import useLoadingState from "hooks/useLoadingState";
import useModalRenderer from "hooks/useModalRenderer";
import usePaginationControls from "hooks/usePaginationControls";
import useTableCustomizationHelper, {
  DummyCustomizationHelper,
  TableCustomizationHelper,
} from "hooks/useTableCustomizationHelper";
import { compact, find, noop } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FindingAPI from "restAPI/FindingAPI";
import { ExportField } from "types/api/exportField";
import { FindingFiltersRequestDto } from "types/api/findingFiltersRequestDto";
import { FindingSummary } from "types/api/findingSummary";
import { ListFindingResponse } from "types/api/listFindingResponse";
import { DummyPaginationControlls, PaginationControls } from "types/pagination";
import FindingDetailsPopup from "views/Findings/FindingDetails/FindingDetailsPopup";
import { FindingModalCommonConfig } from "views/Findings/FindingDetails/common";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";

import { getSortParamForAPI } from "../../../Findings/FindingList/FindingListProvider";

const alwaysVisibleColumnKeys = [
  "id",
  "title",
  "product",
  "subProduct",
  "riskScore",
];

export const columnKeys = [
  "id",
  "title",
  "product",
  "subProduct",
  "riskScore",
  "status",
  "severity",
];

type AssetFindingListContextType = {
  loading: boolean;
  tableCustomizationHelper: TableCustomizationHelper<FindingSummary>;
  dataSource: FindingSummary[];
  activeColumns: ExportField[];
  allColumns: ACTableColumnType<FindingSummary>[];
  totalElements: number;
  paginationControls: PaginationControls;
  assetID: number;
  stats?: { [key: string]: number | null };
};

const AssetFindingListContext =
  React.createContext<AssetFindingListContextType>({
    tableCustomizationHelper: DummyCustomizationHelper,
    dataSource: [],
    loading: false,
    activeColumns: [],
    allColumns: [],
    totalElements: 0,
    paginationControls: DummyPaginationControlls,
    assetID: 0,
  });

type Props = {
  children?: JSX.Element[] | JSX.Element;
  assetID: number;
};

export default function AssetsFindingListProvider({
  children,
  assetID,
}: Props) {
  const [dataSource, setDataSource] = useState<FindingSummary[]>([]);
  const [stats, setStats] = useState<{ [key: string]: number | null }>();
  const cancelRef = useRef(noop);
  const statsCancelRef = useRef(noop);
  const [totalElements, setTotalElements] = useState(0);
  const modalRenderer = useModalRenderer();
  const { loading, setLoading } = useLoadingState();
  const paginationControls = usePaginationControls({
    defaultPageSize: 10,
    defaultSortInfo: { riskScore: "descend" },
  });
  const { findingBreakdownGroupByField } = useGlobalSettingsContext();
  const entityManagerParamsRef = useRef<
    Pick<EntityNavigatorParams, "payload" | "totalEntities">
  >({ payload: {}, totalEntities: 0 });

  const { page, pageSize, sortInfo, filterInfo } = paginationControls;

  const actualAppliedFilters = useDetectFilterChange(filterInfo);

  const fetchData = useCallback(() => {
    cancelRef.current();
    if (assetID) {
      const sortColumns = getSortParamForAPI(sortInfo);
      //
      entityManagerParamsRef.current.payload = {
        page: page - 1,
        size: pageSize,
        sortColumns,
        filters: {
          ...actualAppliedFilters,
          assetId: [assetID],
        },
      };
      //
      const { promise, cancel } = FindingAPI.getAll(
        entityManagerParamsRef.current.payload as FindingFiltersRequestDto,
      );
      promise
        .then((response: ListFindingResponse) => {
          setDataSource(response.content!);
          setTotalElements(response.totalElements!);
          entityManagerParamsRef.current.totalEntities =
            response.totalElements!;
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setLoading(false);
        });

      cancelRef.current = cancel;
      return cancel;
    }
  }, [sortInfo, page, pageSize, assetID, actualAppliedFilters, setLoading]);

  const fetchFindingStats = useCallback(() => {
    statsCancelRef.current();
    if (assetID) {
      const { promise, cancel } = FindingAPI.getStats({
        aggField: "severity",
        filters: {
          ...actualAppliedFilters,
          assetId: [assetID],
        },
        secAggField: findingBreakdownGroupByField,
      } as any);
      promise
        .then((response: any) => {
          const _stats = response.stats.Reported.severity;
          setStats({ ..._stats });
        })
        .catch(() => {
          setStats({});
        });

      statsCancelRef.current = cancel;
      return cancel;
    }
  }, [assetID, actualAppliedFilters, findingBreakdownGroupByField]);

  const showFindingDetail = useCallback(
    (e) => {
      const entityNavigatorParams = entityManagerParamsRef.current;
      const index = +e.currentTarget.dataset.index!;
      const findingId = e.currentTarget.dataset.id;
      modalRenderer.show({
        content: (
          <FindingDetailsPopup
            findingId={findingId}
            findingNavigatorProps={{
              api: FindingAPI.getAll,
              defaultPage:
                entityNavigatorParams.payload.size *
                  entityNavigatorParams.payload.page +
                index,
              ...entityNavigatorParams,
            }}
          />
        ),
        onClose: fetchData,
        ...FindingModalCommonConfig,
      });
    },
    [fetchData, modalRenderer],
  );

  const columns = useFindingColumns({
    showFindingDetail,
    externalColumn: [],
    columnInitialWidths: {
      title: 716,
    },
  });
  const finalColumns = compact(
    columnKeys.map((key: string) => {
      return find(columns, { key });
    }),
  );

  const tableCustomizationHelper = useTableCustomizationHelper({
    columns: finalColumns,
    draggable: true,
    alwaysVisibleKeys: alwaysVisibleColumnKeys,
    defaultHiddenKeys: ["severity", "status"],
    prefKey: "asset-finding",
  });

  const { exportColumns } = tableCustomizationHelper;

  const activeColumns = exportColumns;

  useEffect(() => {
    fetchData();
    fetchFindingStats();
  }, [fetchData, fetchFindingStats]);

  const contextValue = useMemo(() => {
    return {
      dataSource,
      loading,
      tableCustomizationHelper,
      activeColumns,
      allColumns: finalColumns,
      totalElements,
      paginationControls,
      assetID,
      stats,
    };
  }, [
    loading,
    dataSource,
    tableCustomizationHelper,
    activeColumns,
    finalColumns,
    totalElements,
    paginationControls,
    assetID,
    stats,
  ]);

  return (
    <AssetFindingListContext.Provider value={contextValue}>
      {children}
      {modalRenderer.context}
    </AssetFindingListContext.Provider>
  );
}

export function useAssetFindingListDetails() {
  return useContext(AssetFindingListContext);
}
